import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChild } from '../../contexts/ChildContext';
import ProfileEditView from '../../components/ProfileEditView';
import AvatarUpload from '../../components/AvatarUpload';

const F = { fontFamily: 'Inter' } as const;

type View = 'profile' | 'edit-password';

/**
 * Parent Profile — shows the parent's own info plus a list of linked
 * children. Each child row displays real credentials from DB:
 * student code, grade, class, and institution.
 */
const ParentProfile: React.FC = () => {
  const { user } = useAuth();
  const { children: kids, loading: kidsLoading } = useChild();
  const [view, setView] = useState<View>('profile');

  // user.name is "first last" (AuthContext): strip the surname rather than the last
  // word, so a two-word first name or surname is not cut (teacher Profile's rule).
  const ad = user?.name
    ? (user.surname && user.name.endsWith(user.surname) ? user.name.slice(0, -user.surname.length).trim() : '') || user.name
    : '-';
  const soyad = user?.surname || '-';
  const profileFields = [
    { label: 'Ad', value: ad },
    { label: 'Soyad', value: soyad },
    { label: 'E-posta', value: user?.email ?? '-' },
    { label: 'Telefon Numarası', value: (user as any)?.phone ?? '-' },
    { label: 'Meslek', value: (user as any)?.parent_occupation ?? '-' },
  ];

  // AP62: eski görünüm yalnız şifre değiştiriyordu ("Profili Düzenle" adına
  // rağmen ad/e-posta/telefon/Meslek düzenlenemiyordu). Öğrenci/öğretmenle
  // aynı ProfileEditView'a geçildi — bilgi + Meslek + şifre tek yerde.
  if (view === 'edit-password') {
    return (
      <ProfileEditView
        onBack={() => setView('profile')}
        source={user}
        readOnlyRows={[]}
        showOccupation
      />
    );
  }

  return (
    <div className="max-w-[1100px]">
      <h1 className="mb-5" style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Profil</h1>

      {/* AP62 (2. tur): öğretmen profiliyle aynı standart düzen — hero kartı
          (avatar + kimlik + sağda Profili Düzenle) + altında Bilgilerim kartı. */}
      <div className="bg-white rounded-[24px] p-6 mb-5 flex flex-col sm:flex-row sm:items-center gap-5"
        style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
        {/* AP35: avatar artık tıklanıp değiştirilebilir */}
        <AvatarUpload size={112} />
        <div className="flex-1 min-w-0">
          <p style={{ ...F, fontSize: 28, fontWeight: 600, color: '#2b2b2b', marginBottom: 2 }}>{user?.name || 'Veli'}</p>
          <p style={{ ...F, fontSize: 15, fontWeight: 500, color: '#6b6b6b', marginBottom: 10 }}>
            {kids.length > 0 ? `${kids.length} öğrenci velisi` : 'Veli'}
          </p>
          <div className="flex flex-wrap gap-2">
            {kids.map((c) => (
              <span key={c.id} className="rounded-full px-3 py-1" style={{ ...F, fontSize: 13, fontWeight: 600, color: '#3973bf', background: 'rgba(57,115,191,0.10)' }}>
                {c.name} {c.surname}
              </span>
            ))}
          </div>
        </div>
        <button onClick={() => setView('edit-password')}
          className="rounded-[16px] px-5 py-2.5 hover:opacity-90 self-start sm:self-center flex-shrink-0"
          style={{ background: '#e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Profili Düzenle</span>
        </button>
      </div>

      {/* Bilgilerim — öğretmen profiliyle aynı kırılım */}
      <div className="bg-white rounded-[24px] p-6 mb-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
        <h2 className="mb-5" style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Bilgilerim</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-10 gap-y-5">
          {profileFields.map((f, i) => (
            <div key={i} className="min-w-0 border-b border-[#eeeeee] pb-3">
              <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#3973bf', marginBottom: 4 }}>{f.label}</p>
              <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b' }} className="break-words">{f.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Öğrencilerim — gerçek DB verileri (kod, sınıf, kurum) */}
      <h2 style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b', marginBottom: 12 }}>Öğrenci Bilgileri</h2>
      {kidsLoading ? (
        <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : kids.length === 0 ? (
        <p style={{ ...F, fontSize: 14, color: '#888' }}>Henüz bağlı öğrenciniz yok. Çocuğunuz görünmüyorsa okulunuzla iletişime geçin.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {kids.map(c => (
            <div key={c.id} className="bg-white rounded-[16px] p-4 flex items-center gap-4" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <div className="w-[60px] h-[60px] rounded-full bg-[#3973bf] overflow-hidden flex-shrink-0">
                {/* AP62: çocuğun kendi yüklediği fotoğraf veliye de görünsün */}
                <img src={c.avatarUrl || '/a-avatar.png'} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p style={{ ...F, fontSize: 18, fontWeight: 700, color: '#2b2b2b' }} className="truncate">{c.name} {c.surname}</p>
                <div className="space-y-0.5 mt-1">
                  {c.studentCode && <p style={{ ...F, fontSize: 12, color: '#888' }}>Kod: <span style={{ color: '#e5599b', fontWeight: 600 }}>{c.studentCode}</span></p>}
                  {c.className && <p style={{ ...F, fontSize: 13, color: '#3973bf', fontWeight: 600 }}>{c.className}</p>}
                  {c.gradeLevel?.name && <p style={{ ...F, fontSize: 12, color: '#2b2b2b' }}>{c.gradeLevel.name}</p>}
                  {c.institution?.name && <p style={{ ...F, fontSize: 12, color: '#888' }} className="truncate">{c.institution.name}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParentProfile;
